// src/components/user/CreateUser.jsx
import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import {
  createUser,
  getUserById,
  getUserStatus,
  updateUser,
  getRoles,
} from '../store/slices/userSlice';
import { extractList } from '../utils/response';
import {
    Box,
    TextField,
    Button,
    Typography,
    Grid,
    Paper,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    ThemeProvider,
    createTheme,
    InputAdornment,
    IconButton,
    Snackbar,
    Alert,
    CircularProgress,
    Skeleton,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import PhoneIcon from '@mui/icons-material/Phone';
import AlternateEmailIcon from '@mui/icons-material/AlternateEmail';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { USERS_LIST_PATH } from '../navigations/path';
import { useAuth } from '../hooks/useAuth';

// Custom Theme
const revivePayTheme = createTheme({
    palette: {
        primary: { main: '#0a3d62' },
        success: { main: '#4caf50' },
    },
    components: {
        MuiOutlinedInput: {
            styleOverrides: {
                root: { borderRadius: '8px' },
            },
        },
    },
});

// Secure password generator
const generateSecurePassword = (length = 12) => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*()_+~`|}{[]:;?><,./-=';
    let password = '';
    const ensureChars = [
        'ABCDEFGHIJKLMNOPQRSTUVWXYZ'[Math.floor(Math.random() * 26)],
        'abcdefghijklmnopqrstuvwxyz'[Math.floor(Math.random() * 26)],
        '0123456789'[Math.floor(Math.random() * 10)],
        '!@#$%^&*()_+~`|}{[]:;?><,./-='[Math.floor(Math.random() * 20)],
    ];

    const array = new Uint8Array(length - ensureChars.length);
    if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
        crypto.getRandomValues(array);
    } else {
        for (let i = 0; i < array.length; i++) {
            array[i] = Math.floor(Math.random() * 256);
        }
    }

    for (let i = 0; i < array.length; i++) {
        password += chars[array[i] % chars.length];
    }

    password = (ensureChars.join('') + password).split('').sort(() => 0.5 - Math.random()).join('');
    return password;
};

const CreateUser = () => {
  const dispatch = useDispatch();
  const { hasPermission } = useAuth();

  const canViewUser = hasPermission('user-view');
  const canEditUser = hasPermission('user-edit');

    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const editId = searchParams.get('edit_id');
    const viewId = searchParams.get('view');
    const isEditMode = !!editId;
    const isViewMode = !!viewId;
    const userId = editId || viewId;

    const initialFormData = {
        name: '',
        username: '',
        email: '',
        password: isEditMode || isViewMode ? '' : generateSecurePassword(),
        phone: '',
        gender: '',
        role: '',
        status: '',
    };
    const [formData, setFormData] = useState(initialFormData);
    const [errors, setErrors] = useState({});
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isLoading, setIsLoading] = useState(!!userId);
    const [statuses, setStatuses] = useState([]);
    const [roles, setRoles] = useState([]);        // NEW: Roles from API
    const [loadingRoles, setLoadingRoles] = useState(true); // NEW: Roles loading

    // Fetch statuses, roles, and user data
    useEffect(() => {
        const fetchStatuses = async () => {
            try {
                const statusData = await dispatch(getUserStatus()).unwrap();
                setStatuses(Array.isArray(statusData) ? statusData : statusData?.list ?? []);
            } catch (error) {
                console.error("Fetch statuses error:", error);
                toast.error('Failed to load statuses.');
            }
        };

        const fetchRoles = async () => {
            try {
                setLoadingRoles(true);
                const response = await dispatch(getRoles()).unwrap();
                const roleList = extractList(response);
                setRoles(roleList);
            } catch (error) {
                console.error("Fetch roles error:", error);
                toast.error('Failed to load roles.');
            } finally {
                setLoadingRoles(false);
            }
        };

        const fetchUser = async () => {
            if(!canEditUser && !canViewUser) return
            if (userId) {
                setIsLoading(true);
                try {
                    const userData = await dispatch(getUserById(userId)).unwrap();
                    const user = userData?.data ?? userData;
                    const userRoleId = user?.roles?.[0]?.id || '';
                    setFormData({
                        name: user?.name || '',
                        username: user?.username || '',
                        email: user?.email || '',
                        password: '',
                        phone: user?.phone || '',
                        gender: user?.gender || '',
                        role: userRoleId || '',
                        status: user?.status?.id || '',
                    });
                } catch (error) {
                    console.error("Fetch user error:", error);
                    toast.error('Failed to load user data.');
                } finally {
                    setIsLoading(false);
                }
            }
        };

        fetchStatuses();
        fetchRoles();
        fetchUser();
    }, [userId]);

    const validateForm = (data) => {
        const validationErrors = {};
        if (!data.name.trim()) validationErrors.name = 'Full Name is required.';
        if (!data.username.trim()) validationErrors.username = 'Username is required.';
        else if (!/^[a-zA-Z0-9_]{3,15}$/.test(data.username)) validationErrors.username = 'Username must be 3-15 alphanumeric characters or underscores.';

        if (!data.email.trim()) validationErrors.email = 'Email is required.';
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) validationErrors.email = 'Invalid email format.';

        if (!isEditMode && !data.password.trim()) validationErrors.password = 'Initial Password is required for new users.';
        if (!data.status) validationErrors.status = 'Status is required.';
        if (!data.role) validationErrors.role = 'Role is required.'; // NEW

        return validationErrors;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setErrors(prev => ({ ...prev, [name]: '' }));

        if (name === 'phone') {
            const digits = value.replace(/\D/g, '');
            setFormData(prev => ({ ...prev, [name]: digits }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleGeneratePassword = () => {
        const newPassword = generateSecurePassword();
        setFormData(prev => ({ ...prev, password: newPassword }));
        setErrors(prev => ({ ...prev, password: '' }));
    };

    const handleCopyPassword = () => {
        if (navigator.clipboard) {
            navigator.clipboard.writeText(formData.password)
                .then(() => setSnackbarOpen(true))
                .catch(() => toast.error('Failed to copy password.'));
        } else {
            const tempInput = document.createElement('input');
            tempInput.value = formData.password;
            document.body.appendChild(tempInput);
            tempInput.select();
            document.execCommand('copy');
            document.body.removeChild(tempInput);
            setSnackbarOpen(true);
        }
    };

    const handleCloseSnackbar = (event, reason) => {
        if (reason === 'clickaway') return;
        setSnackbarOpen(false);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const validationData = isEditMode && !formData.password.trim()
            ? { ...formData, password: 'DUMMY_PASSWORD' }
            : formData;

        const validationErrors = validateForm(validationData);
        setErrors(validationErrors);

        if (Object.keys(validationErrors).length) {
            toast.error('Please fix the errors in the form.');
            return;
        }

        setIsSubmitting(true);
        try {
            const payload = isEditMode && !formData.password.trim()
                ? Object.keys(formData).reduce((acc, key) => {
                    if (key !== 'password') {
                        if (key === 'status') {
                            acc['status_id'] = formData[key];
                        } else if (key === 'role') {
                            // Send role name instead of ID
                            const selectedRole = roles.find(r => r.id === formData[key]);
                            acc['role'] = selectedRole?.name || '';
                        } else {
                            acc[key] = formData[key];
                        }
                    }
                    return acc;
                }, {})
                : {
                    ...formData,
                    status_id: formData.status,
                    // Send role name
                    role: roles.find(r => r.id === formData.role)?.name || '',
                    status: undefined,
                    role_id: undefined, // remove if not needed
                };

            let response;
            if (isEditMode) {
                response = await dispatch(updateUser({ id: userId, userData: payload })).unwrap();
                toast.success(`User ${formData.name} updated successfully!`);
            } else {
                response = await dispatch(createUser(payload)).unwrap();
                toast.success(`User ${formData.name} created successfully! (ID: ${response.userId || 'N/A'})`);
            }

            navigate(USERS_LIST_PATH);
        } catch (error) {
            console.error("API submission error:", error);
            if (error.response?.data?.errors) {
                error.response.data.errors.forEach((err) => toast.error(err.message));
            } else {
                toast.error(isEditMode ? 'Failed to update user.' : 'Failed to create user. Please check the data.');
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                <CircularProgress color="primary" />
            </Box>
        );
    }

    const formTitle = isViewMode
        ? `View User Account: ${formData.name}`
        : isEditMode
            ? `Edit User Account: ${formData.name}`
            : 'Create New User Account';
    const submitButtonText = isEditMode ? 'Update User' : 'Create User';
    const isFormDisabled = isViewMode || isSubmitting;

    return (
        <ThemeProvider theme={revivePayTheme}>
            <Box sx={{ flexGrow: 1, p: { xs: 2, md: 4 }, bgcolor: '#fff', minHeight: '100vh', fontFamily: 'Inter, sans-serif' }}>
                <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main', mb: 4 }}>
                    {formTitle}
                </Typography>

                <Paper elevation={4} sx={{ p: { xs: 2, md: 4 }, borderRadius: 3, maxWidth: '900px', mx: 'auto' }}>
                    <Typography variant="h6" component="h2" gutterBottom sx={{ mb: 3, borderBottom: '2px solid #eee', pb: 1, color: revivePayTheme.palette.primary.main }}>
                        User Details
                    </Typography>

                    <form onSubmit={handleSubmit}>
                        <Grid container spacing={3}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    
                                    fullWidth
                                    label="Full Name"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    variant="outlined"
                                    disabled={isFormDisabled}
                                    error={!!errors.name}
                                    helperText={errors.name}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    
                                    fullWidth
                                    label="Email Address"
                                    name="email"
                                    type="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    variant="outlined"
                                    disabled={isFormDisabled}
                                    error={!!errors.email}
                                    helperText={errors.email || (isEditMode ? '' : "Try 'fail@example.com' to simulate an API error.")}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <AlternateEmailIcon color="primary" />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <TextField
                                    
                                    fullWidth
                                    label="Username"
                                    name="username"
                                    value={formData.username}
                                    onChange={handleChange}
                                    variant="outlined"
                                    disabled={isFormDisabled}
                                    error={!!errors.username}
                                    helperText={errors.username || "Must be unique and 3-15 alphanumeric characters."}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth disabled={isFormDisabled}>
                                    <InputLabel id="gender-label">Gender</InputLabel>
                                    <Select
                                        labelId="gender-label"
                                        name="gender"
                                        value={formData.gender}
                                        label="Gender"
                                        onChange={handleChange}
                                    >
                                        <MenuItem value=""><em>Not Specified</em></MenuItem>
                                        <MenuItem value="M">Male</MenuItem>
                                        <MenuItem value="F">Female</MenuItem>
                                        <MenuItem value="O">Other</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <TextField
                                  
                                    fullWidth
                                    label={isEditMode ? "New Password (Leave blank to keep old)" : "Initial Password"}
                                    name="password"
                                    type="text"
                                    value={formData.password}
                                    onChange={handleChange}
                                    variant="outlined"
                                    disabled={isFormDisabled}
                                    error={!!errors.password}
                                    helperText={errors.password || (isEditMode ? "Leave blank to keep current password." : "Securely generated default password.")}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <VpnKeyIcon color="primary" />
                                            </InputAdornment>
                                        ),
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <Button
                                                    onClick={handleGeneratePassword}
                                                    startIcon={<VisibilityOffIcon />}
                                                    color="primary"
                                                    size="small"
                                                    sx={{ mr: 1, whiteSpace: 'nowrap' }}
                                                    disabled={isFormDisabled}
                                                >
                                                    Generate
                                                </Button>
                                                <IconButton
                                                    onClick={handleCopyPassword}
                                                    edge="end"
                                                    aria-label="copy password"
                                                    color="primary"
                                                    size="small"
                                                    disabled={isFormDisabled}
                                                >
                                                    <ContentCopyIcon />
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>

                            {/* DYNAMIC ROLES SELECT */}
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth  disabled={isFormDisabled} error={!!errors.role}>
                                    <InputLabel id="role-label">Role</InputLabel>
                                    <Select
                                        labelId="role-label"
                                        name="role"
                                        value={formData.role}
                                        label="Role"
                                        onChange={handleChange}
                                        searchable={true}
                                    >
                                        {loadingRoles ? (
                                            <MenuItem disabled>
                                                <Skeleton width={120} />
                                            </MenuItem>
                                        ) : roles.length > 0 ? (
                                            roles.map((role) => (
                                                <MenuItem key={role.id} value={role.id}>
                                                    {role.name}
                                                </MenuItem>
                                            ))
                                        ) : (
                                            <MenuItem value="" disabled>
                                                No roles available
                                            </MenuItem>
                                        )}
                                    </Select>
                                    {!!errors.role && (
                                        <Typography variant="caption" color="error" sx={{ ml: 1.5 }}>
                                            {errors.role}
                                        </Typography>
                                    )}
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Phone Number"
                                    name="phone"
                                    type="tel"
                                    value={formData.phone}
                                    onChange={handleChange}
                                    variant="outlined"
                                    disabled={isFormDisabled}
                                    error={!!errors.phone}
                                    helperText={errors.phone}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <PhoneIcon color="primary" />
                                            </InputAdornment>
                                        ),
                                        inputProps: { maxLength: 15 },
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth  disabled={isFormDisabled} error={!!errors.status}>
                                    <InputLabel id="status-label">User Status</InputLabel>
                                    <Select
                                        labelId="status-label"
                                        name="status"
                                        value={formData.status}
                                        label="User Status"
                                        onChange={handleChange}
                                    >
                                        {statuses.length > 0 ? (
                                            statuses.map((status) => (
                                                <MenuItem key={status.id} value={status.id}>
                                                    {status.name.charAt(0).toUpperCase() + status.name.slice(1)}
                                                </MenuItem>
                                            ))
                                        ) : (
                                            <MenuItem value="" disabled>
                                                No statuses available
                                            </MenuItem>
                                        )}
                                    </Select>
                                    {!!errors.status && (
                                        <Typography variant="caption" color="error" sx={{ ml: 1.5 }}>
                                            {errors.status}
                                        </Typography>
                                    )}
                                </FormControl>
                            </Grid>

                            {!isViewMode && (
                                <Grid item xs={12}>
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4 }}>
                                        <Button
                                            type="submit"
                                            variant="contained"
                                            color="primary"
                                            size="large"
                                            startIcon={isSubmitting ? null : <PersonAddIcon />}
                                            disabled={isSubmitting}
                                            sx={{ py: 1.5, px: 4, borderRadius: 2, boxShadow: 6, transition: 'transform 0.2s', '&:hover': { transform: 'translateY(-2px)', boxShadow: 10 } }}
                                        >
                                            {isSubmitting ? <CircularProgress size={24} color="inherit" /> : submitButtonText}
                                        </Button>
                                    </Box>
                                </Grid>
                            )}
                        </Grid>
                    </form>
                </Paper>

                <Snackbar open={snackbarOpen} autoHideDuration={3000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                    <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%', borderRadius: 1 }}>
                        Password copied to clipboard!
                    </Alert>
                </Snackbar>
            </Box>
        </ThemeProvider>
    );
};

export default CreateUser;